//# allFunctionsCalledOnLoad

import Swiper from "swiper";
import {
	Navigation,
	Keyboard,
	A11y,
	EffectFade,
	Autoplay,
	FreeMode,
} from "swiper/modules";

import "swiper/swiper.min.css";
import "swiper/modules/keyboard.min.css";
import "swiper/modules/navigation.min.css";
import "swiper/modules/A11y.min.css";
import "swiper/modules/free-mode.min.css";

Swiper.use([Navigation, Keyboard, A11y, EffectFade, Autoplay, FreeMode]);

import {DotLottie} from "@lottiefiles/dotlottie-web";

import {
	animate,
	inView,
	cubicBezier,
	stagger,
	scroll,
} from "motion/dist/motion.js";
import {listen} from "./quicklink.umd.js";

const easeOutExpo = cubicBezier(0.35, 0.75, 0.5, 1);
const easeOutExpo2 = cubicBezier(0.2, 0.95, 0.4, 1);

/* Helper functions */
function setItemWithExpiry(key, value, ttl) {
	const now = new Date();

	const item = {
		value: value,
		expiry: Math.round(now.getTime() / 1000) + ttl,
	};

	localStorage.setItem(key, JSON.stringify(item));
}

function getItemWithExpiry(key) {
	const itemStr = localStorage.getItem(key);

	// Check if item exists before parsing
	if (!itemStr) {
		return null;
	}

	const item = JSON.parse(itemStr);
	const now = new Date();
	const nowInSeconds = Math.round(now.getTime() / 1000);

	// Check if expired
	if (nowInSeconds > item.expiry) {
		localStorage.removeItem(key);
		return null;
	}

	// Return both the value and remaining minutes
	return {
		value: item.value,
		remainingMinutes: Math.round((item.expiry - nowInSeconds) / 60),
	};
}

// Resize listener setup with viewport change filtering
function setupResizeListener(callback, delay = 100) {
	let resizeTimeout;
	let prevWidth = window.innerWidth;
	let prevHeight = window.innerHeight;

	window.addEventListener("resize", () => {
		clearTimeout(resizeTimeout); // Debounce the resize event
		resizeTimeout = setTimeout(() => {
			const currentWidth = window.innerWidth;
			const currentHeight = window.innerHeight;

			// Determine if resize is significant
			const isWidthChanged = currentWidth !== prevWidth;
			const isHeightSignificant =
				Math.abs(currentHeight - prevHeight) > 50;

			if (isWidthChanged || isHeightSignificant) {
				// Call the provided callback only on significant changes
				callback();

				// Update the stored dimensions
				prevWidth = currentWidth;
				prevHeight = currentHeight;
			}
		}, delay);
	});
}

// Check the reduced-motion preference and set localy
function reducedMotion() {
	const reducedMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)"
	).matches;

	localStorage.setItem("reduced-motion", reducedMotion);
}

// Manual toggle off animations
function toggleMotion() {
	const button = document.getElementById("toggle-reduced-motion");
	const manualReducedMotion = localStorage.getItem("manual-reduced-motion");

	button?.addEventListener("click", (event) => {
		localStorage.setItem("manual-reduced-motion", newState);
	});
}

function toggleDarkmode() {
	const button = document.getElementById("toggle_theme");
	button?.addEventListener("click", () => {
		document.documentElement.classList.toggle("dark");
		if (localStorage.theme === "light") {
			localStorage.theme = "dark";
		} else if (localStorage.theme === "dark") {
			localStorage.theme = "light";
		} else {
			localStorage.theme = "dark";
		}
	});
}

function filterCase() {
	const filterContainer = document.querySelector("#filterContainer");
	if (!filterContainer) {
		return;
	}

	const filterButtons = document.querySelectorAll("button[data-filter]");
	const clearButton = filterButtons[0];
	const links = filterContainer?.querySelectorAll("article");

	let activeFilters = new Set();

	function filterLinks() {
		const activeFiltersArray = Array.from(activeFilters);

		links?.forEach((link) => {
			const linkCategories = link.dataset.filter.split(",");
			let showLink = false;

			if (activeFilters.size === 0) {
				showLink = true;
			} else {
				showLink = activeFiltersArray.every((filter) =>
					linkCategories.includes(filter)
				);
			}

			if (showLink) {
				link.classList.remove("hidden");
			} else {
				link.classList.add("hidden");
			}
		});

		updateURL();
	}

	function updateURL() {
		const urlParams = new URLSearchParams();

		if (activeFilters.size > 0) {
			const filterString = Array.from(activeFilters).join("&");
			urlParams.set("filter", filterString);
		}

		const currentHash = window.location.hash || "";
		const newURL = `${window.location.pathname}${
			urlParams.toString() ? "?" + urlParams.toString() : ""
		}${currentHash}`;

		window.history.replaceState(null, null, newURL);
	}

	function handleURLParams() {
		const urlParams = new URLSearchParams(window.location.search);
		const filterParam = urlParams.get("filter");

		if (filterParam) {
			// Split by "&" since that's how we store them
			filterParam.split("&").forEach((filter) => {
				activeFilters.add(filter);
			});
		}
	}

	// 1. Handle URL parameters *first*
	handleURLParams();

	// 2. Attach event listeners *after* handling URL parameters
	filterButtons?.forEach((button) => {
		button.addEventListener("click", () => {
			const filter = button.dataset.filter;

			if (button === clearButton) {
				activeFilters.clear();
				filterButtons.forEach((btn) => btn.classList.remove("active"));
				button.classList.add("active");
			} else {
				clearButton.classList.remove("active");
				if (activeFilters.has(filter)) {
					activeFilters.delete(filter);
					button.classList.remove("active");
				} else {
					activeFilters.add(filter);
					button.classList.add("active");
				}
			}

			if (activeFilters.size === 0) {
				clearButton.classList.add("active");
			}

			filterLinks();
		});
	});

	// 3. Perform initial filtering *after* attaching event listeners and setting activeFilters
	filterLinks();

	// 4. Set active button styles after the initial filter
	activeFilters.forEach((filter) => {
		const button = document.querySelector(`[data-filter="${filter}"]`);
		if (button) {
			button.classList.add("active");
			clearButton.classList.remove("active");
		}
	});
}

function toggleTabsContent(
	buttonSelector,
	contentSelector,
	activeClass = "active"
) {
	const buttons = document.querySelectorAll(buttonSelector);
	const contents = document.querySelectorAll(contentSelector);

	buttons.forEach((button) => {
		button.addEventListener("click", () => {
			const tabValue = button.getAttribute("data-tab");

			buttons.forEach((btn) => btn.classList.remove(activeClass));

			contents.forEach((content) => content.classList.add("hidden"));

			button.classList.add(activeClass);

			const targetContent = document.querySelector(
				`${contentSelector}[data-tab="${tabValue}"]`
			);
			if (targetContent) {
				targetContent.classList.remove("hidden");
			}
		});
	});
}

/* Needs update? */
function toggleOverlays(duration) {
	const hamburgerButton = document.getElementById("hamburgerButton");
	const backdrop = document.getElementById("backdropOverlay");
	const menuTakeover = document.getElementById("menuTakeover");
	const menuBackdrop = document.getElementById("menuTakeoverBackdrop");
	const focusableElements = menuTakeover.querySelectorAll(
		"a, button, input, [tabindex]"
	);

	// Initialize focus state
	const setFocusable = (enabled) => {
		focusableElements.forEach((el) => {
			el.tabIndex = enabled ? 0 : -1;
		});

		if (enabled) {
			menuTakeover.removeAttribute("inert");
			menuTakeover.setAttribute("aria-hidden", "false");
		} else {
			menuTakeover.setAttribute("inert", "");
			menuTakeover.setAttribute("aria-hidden", "true");
		}
	};

	setFocusable(false); // Disable focus initially

	const motionOpenMenu = [
		[menuTakeover, {width: ["0%", "100%"]}, {ease: easeOutExpo2}],
	];

	const motionCloseMenu = [
		[menuTakeover, {width: ["100%", "0%"]}, {ease: easeOutExpo2}],
	];

	const bodyDark = () =>
		animate(
			menuBackdrop,
			{opacity: ["0%", "100%"]},
			{duration, ease: easeOutExpo2}
		);

	const bodyLight = () =>
		animate(
			menuBackdrop,
			{opacity: ["100%", "0%"]},
			{duration, ease: easeOutExpo2}
		);

	let isMenuOpen = false;

	const toggleMenu = () => {
		if (!isMenuOpen) {
			const openMenu = animate(motionOpenMenu, {
				defaultTransition: {duration: duration, ease: easeOutExpo2},
			});
			bodyDark();
			openMenu.then(() => {
				setFocusable(true);
			});
		} else {
			const closeMenu = animate(motionCloseMenu, {
				defaultTransition: {duration: duration, ease: easeOutExpo2},
			});
			bodyLight();
			closeMenu.then(() => {
				setFocusable(false);
				hamburgerButton.focus(); // Move focus back to the hamburger button
			});
		}
		isMenuOpen = !isMenuOpen;
	};

	hamburgerButton?.addEventListener("click", toggleMenu);
	backdrop?.addEventListener("click", toggleMenu);
}

/* Basic Page-Transition with bfcache Fix */
function pageFadeTransition(fadeInMs) {
	const loader = document.getElementById("loader");
	const navbar = document.querySelector("header");
	const firsVisit = getItemWithExpiry("first-visit");
	const logoElement = document.getElementById("logo-link");
	const loadingGif = document.getElementById("loading-gif");

	if (!loader) return;

	loader.style.transitionDuration = fadeInMs + "ms";

	// Handle initial page load and bfcache
	window.addEventListener("pageshow", (event) => {
		if (!event.persisted && !firsVisit) {
			// DO Animation here
			setTimeout(() => {
				console.log("Fancy loading animation done (2s)");
				hideLoader(fadeInMs);
			}, 25);
			setItemWithExpiry("first-visit", "value", 3600); // 1 hours
		} else if (!event.persisted) {
			hideLoader(fadeInMs);
			setItemWithExpiry("first-visit", "value", 3600); // 1 hours
		} else {
			console.warn("bfcache");
			setTimeout(() => {
				hideLoader(fadeInMs);
			}, 25);
		}
	});

	// Transition out the page on link click
	document.querySelectorAll("a").forEach((link) => {
		if (
			link.hostname === window.location.hostname &&
			!link.hash &&
			!link.hasAttribute("download")
		) {
			link.addEventListener("click", function (event) {
				event.preventDefault();

				loader.style.visibility = "visible";
				loader.classList.add("visible");
				hideNavbar(navbar);

				setTimeout(() => {
					window.location = this.href;
				}, fadeInMs); // Match this to the CSS transition duration
			});
		}
	});

	function hideLoader(delay) {
		setTimeout(() => {
			loader.classList.remove("visible");
			showNavbar(navbar);
			setTimeout(() => {
				loader.style.visibility = "hidden";
			}, delay);
		}, 25);
	}

	function hideNavbar(element) {
		if (!element) {
			console.warn("navbar not found");
			return;
		}
		//LogoAnimation.playOut();
		element.style.transform = "none";
		animate(element, {y: [0, -80]}, {duration: 0.5, ease: easeOutExpo});
	}

	function showNavbar(element) {
		if (!element) {
			console.warn("navbar not found");
			return;
		}
		element.style.transform = "none";
		animate(element, {y: [-80, 0]}, {duration: 0.5, ease: easeOutExpo});
		loadingGif.classList.add("hidden");

		if (!firsVisit) {
			logoElement.style.pointerEvents = "none";
			LogoAnimation.playIn();
			setTimeout(() => {
				LogoAnimation.playOut();
				setTimeout(() => {
					logoElement.style.pointerEvents = "auto";
				}, 1000);
			}, 2500);
		}
	}
}

// Lazy load videos
function lazyLoadVideos() {
	const lazyVideoElements = document.querySelectorAll(
		"video[data-desktop-src]:not(.hero-video-manual-load)"
	);
	if (!lazyVideoElements.length) return;

	if (!("IntersectionObserver" in window)) return; // Early exit if no observer

	const lazyVideoObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return; // Skip if not intersecting

				const video = entry.target;
				handleResponsiveVideoSource(video);
				if (video.hasAttribute("pausable")) {
					setTimeout(() => setupVideoPauseButton(video), 50);
				}
				lazyVideoObserver.unobserve(video); // Stop observing once loaded
			});
		},
		{rootMargin: "0px 0px 200px 0px"}
	);

	lazyVideoElements?.forEach((video) => lazyVideoObserver.observe(video));

	window.addEventListener("resize", () => {
		lazyVideoElements.forEach(handleResponsiveVideoSource);
	});
}

// Handle responsive video sources
function handleResponsiveVideoSource(video) {
	const videoSource = video.querySelector("source");
	const desktopSrc = video.getAttribute("data-desktop-src");
	const mobileSrc = video.getAttribute("data-mobile-src");
	const breakpoint = 768;
	const isMobile = window.innerWidth < breakpoint;
	const newSrc = isMobile && mobileSrc ? mobileSrc : desktopSrc;

	if (videoSource.dataset.src !== newSrc) {
		const wasPaused =
			sessionStorage.getItem(`videoPaused-${video.id}`) === "true";

		videoSource.dataset.src = newSrc;
		videoSource.src = newSrc;
		video.load();

		if (wasPaused) {
			video.pause();
		}
	}
}

// Set up video pause button functionality
function setupVideoPauseButton(video) {
	const button = video.closest(".video-wrapper")?.querySelector("button");
	if (!button) return;

	const playIcon = button.querySelector('img[alt="play video"]');
	const pauseIcon = button.querySelector('img[alt="pause video"]');
	const reducedMotion = localStorage.getItem("reduced-motion") == "true";
	let videoPaused =
		sessionStorage.getItem(`videoPaused-${video.id}`) == "true";

	// Initial pause for reduced-motion users
	if (reducedMotion) {
		video.currentTime = 1;
		video.pause();
		videoPaused = true;
	} else if (!videoPaused) {
		video.play();
	}

	// Sync UI state
	playIcon.classList.toggle("hidden", !videoPaused);
	pauseIcon.classList.toggle("hidden", videoPaused);

	button?.addEventListener("click", () => {
		if (videoPaused) {
			video.play();
			sessionStorage.setItem(`videoPaused-${video.id}`, "false");
		} else {
			video.pause();
			sessionStorage.setItem(`videoPaused-${video.id}`, "true");
		}
		videoPaused = !videoPaused;
		playIcon.classList.toggle("hidden", !videoPaused);
		pauseIcon.classList.toggle("hidden", videoPaused);
	});

	function setButtonWrapperHeight() {
		const buttonWrapper = button?.closest("div");
		const mediaWrapper = video
			.closest(".video-wrapper")
			?.querySelector("figure, .figure");
		//console.log(mediaWrapper.offsetHeight);

		const mediaHeight = mediaWrapper?.clientHeight;

		buttonWrapper.style.height = mediaHeight + "px";
	}

	setupResizeListener(setButtonWrapperHeight);
	setButtonWrapperHeight();
}

function lazyLoadVimeoEmbed() {
	const vimeoEmbeds = document.querySelectorAll("iframe.vimeoplayer");

	vimeoEmbeds?.forEach((iframe) => {
		const src = iframe.dataset.src;

		inView(
			iframe,
			() => {
				iframe.src = src;
			},
			{
				margin: "50%",
			}
		);
	});
}

// Details Accordion animation
function detailsAccordionAnimation() {
	document.querySelectorAll("details").forEach((detail) => {
		const title = detail.querySelector("summary") ?? null;
		const content = detail.querySelector("[data-content]") ?? null;

		if (content) content.style.height = "0px";

		title?.addEventListener("click", (event) => {
			event.preventDefault();

			if (!detail.open) {
				detail.open = true;
				const open = animate(
					content,
					{height: "auto"},
					{duration: 0.5, ease: easeOutExpo}
				);
				open.then(() => {
					//console.log("details-opened"); // Reset animate(Scroll)??
				});
			} else {
				const close = animate(
					content,
					{height: 0},
					{duration: 0.3, ease: easeOutExpo}
				);
				close.then(() => {
					detail.open = false; //console.log("details-closed"); // Reset animate(Scroll)??
				});
			}
		});
	});
}

// Swiper Slides
function loadSlides() {
	const galleryAuto = new Swiper(".gallery-auto", {
		modules: [Navigation, Keyboard, A11y],
		loop: false,
		slidesPerView: "auto",
		spaceBetween: 24,
		keyboard: {
			enabled: true,
			onlyInViewport: true,
		},
		a11y: {
			enabled: true,
			prevSlideMessage: "Tidigare bild",
			nextSlideMessage: "Nästa bild",
			firstSlideMessage: "Första bilden",
			lastSlideMessage: "Sista bilden",
		},
		navigation: {
			nextEl: ".swiper-btn-next",
			prevEl: ".swiper-btn-prev",
		},
	});

	const galleryFullWidth = new Swiper(".gallery-full", {
		modules: [Navigation, Keyboard, A11y, EffectFade],
		loop: true,
		slidesPerView: 1,
		speed: 1000,
		effect: "fade",
		fadeEffect: {
			crossFade: true,
		},
		keyboard: {
			enabled: true,
		},
		a11y: {
			enabled: true,
			prevSlideMessage: "Tidigare bild",
			nextSlideMessage: "Nästa bild",
			firstSlideMessage: "Första bilden",
			lastSlideMessage: "Sista bilden",
		},
		navigation: {
			nextEl: ".swiper-btn-next",
			prevEl: ".swiper-btn-prev",
		},
	});

	const galleryFullMinimal = new Swiper(".gallery-full-minimal", {
		modules: [EffectFade, Autoplay],
		slidesPerView: 1,
		autoplay: {
			delay: 5000,
			disableOnInteraction: false,
			waitForTransition: false,
		},
		effect: "fade",
		fadeEffect: {
			crossFade: true,
		},
	});

	const logoScroller = new Swiper(".awards-marquee", {
		modules: [Autoplay, FreeMode],
		slidesPerView: "auto",
		spaceBetween: 0,
		loop: true,
		freeMode: {
			enabled: true,
			momentum: false,
		},
		autoplay: {
			delay: 0, // no delay between transitions
			disableOnInteraction: false,
		},

		speed: 6000, // lower = slower, higher = faster
	});
}

function customCursor() {
	const cursorWrapper = document.getElementById("dot-cursor"),
		dot = document.getElementById("dot"),
		dotText = document.getElementById("dot-text"),
		hoverTargetsText = document.querySelectorAll("[data-cursor-text]"),
		hoverTargetsInvert = document.querySelectorAll("[data-cursor-invert]");

	let mouseX = 0,
		mouseY = 0,
		rafActive = false,
		activeHoverTarget = null;

	document.addEventListener("mousemove", (e) => {
		mouseX = e.pageX;
		mouseY = e.pageY - window.scrollY;

		if (!rafActive) {
			rafActive = true;
			requestAnimationFrame(updateCursor);
		}
	});

	function updateCursor() {
		cursorWrapper.style.transform = `translate(${mouseX}px, ${mouseY}px)`;
		rafActive = false; // Allow next frame to be requested
	}

	hoverTargetsText?.forEach((target) => {
		if (target.getAttribute("data-cursor-text") !== "") {
			target.addEventListener("mouseenter", () => {
				activeHoverTarget = target;

				const text = target.getAttribute("data-cursor-text"),
					span = document.createElement("span");

				span.innerHTML = text;
				dotText.innerHTML = "";
				dotText.appendChild(span);

				dotText.style.clipPath =
					"polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
			});
			target.addEventListener("mouseleave", () => {
				activeHoverTarget = null;
				dotText.style.clipPath =
					"polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)";

				dotText.addEventListener(
					"transitionend",
					() => {
						if (activeHoverTarget === null) {
							dotText.innerHTML = "";
						}
					},
					{once: true}
				);
			});
		}
	});

	document.addEventListener("mouseover", (e) => {
		const target = e.target.closest("[data-cursor-invert]");
		if (target) dot.classList.add("invert");
	});

	document.addEventListener("mouseout", (e) => {
		const target = e.target.closest("[data-cursor-invert]");
		if (target) dot.classList.remove("invert");
	});
}

/* Move image within parent element */
/* function moveImage() {
	let section = document.querySelectorAll("[data-hoverList]");
	if (section.length === 0) return;

	const isMobile = () => window.innerWidth < 768;

	section.forEach((section) => {
		let listRows = section.querySelectorAll("[data-hoverList-row]"),
			mediaContainer = section.querySelector("[data-hover-media]"),
			media = mediaContainer.querySelectorAll("[data-counter]"),
			offset = mediaContainer.getBoundingClientRect().height / 2,
			targetX = 0,
			targetY = 0,
			currentX = 0,
			currentY = 0,
			rafActive = false;
		const ease = 0.2;
		let mouseMoveThrottle = false;
		let inViewCleanups = [];

		// Mobile: Position media container centrally
		function positionMediaForMobile() {
			if (isMobile()) {
				mediaContainer.style.transform = `translate(${0}px, ${0}px)`;
			}
		}

		// Desktop: Mouse tracking
		section.addEventListener("mousemove", (e) => {
			if (isMobile()) return;

			const rect = section.getBoundingClientRect();

			if (mouseMoveThrottle) return;
			mouseMoveThrottle = true;
			setTimeout(() => (mouseMoveThrottle = false), 1);

			targetX = e.clientX - rect.left;
			targetY = e.clientY - rect.top - offset;
			if (!rafActive) {
				rafActive = true;
				requestAnimationFrame(moveMediaContainer);
			}
		});

		function moveMediaContainer() {
			if (mediaContainer.length === 0) return;

			currentX += (targetX - currentX) * ease;
			currentY += (targetY - currentY) * ease;

			mediaContainer.style.transform = `translate(${currentX}px, ${currentY}px)`;
			if (
				Math.abs(targetX - currentX) > 0.1 ||
				Math.abs(targetY - currentY) > 0.1
			) {
				requestAnimationFrame(moveMediaContainer);
			} else {
				rafActive = false;
			}
		}

		listRows?.forEach((row) => {
			const counter = row.getAttribute("data-counter");

			const showMedia = () => {
				mediaContainer.classList.remove("opacity-0");

				media.forEach((element) => {
					if (element.getAttribute("data-counter") === counter) {
						element.classList.remove("opacity-0");
					} else {
						element.classList.add("opacity-0");
					}
				});
			};

			const hideMedia = () => {
				mediaContainer.classList.add("opacity-0");
			};

			// Desktop: Mouse events
			row.addEventListener("mouseenter", () => {
				if (!isMobile()) showMedia();
			});
			row.addEventListener("mouseleave", () => {
				if (!isMobile()) hideMedia();
			});

			// Keyboard
			row.addEventListener("focusin", showMedia);
			row.addEventListener("focusout", hideMedia);

			row.addEventListener("focus", () => {
				if (isMobile()) return;

				const rect = row.getBoundingClientRect();
				const sectionRect = section.getBoundingClientRect();

				const newX = rect.left - sectionRect.left + rect.width + 16;
				const newY =
					rect.top - sectionRect.top + rect.height / 2 - offset;

				targetX = newX;
				targetY = newY;

				if (!rafActive) {
					rafActive = true;
					requestAnimationFrame(moveMediaContainer);
				}
			});

			try {
				// Calculate the row height to create a centered trigger zone
				const rowHeight = row.getBoundingClientRect().height;
				const viewportHeight = window.innerHeight;
				const topMargin = viewportHeight / 2 - rowHeight / 2;
				const bottomMargin = topMargin;

				const cleanup = inView(
					row,
					(element, enterInfo) => {
						if (isMobile()) {
							row.classList.add("!opacity-100");
							showMedia();
							positionMediaForMobile();
						}

						// Return the leave callback
						return (leaveInfo) => {
							console.log("leaving element");
							if (isMobile()) {
								hideMedia();
								row.classList.remove("!opacity-100");
							}
						};
					},
					{
						margin: `-${topMargin}px 0px -${bottomMargin}px 0px`,
						amount: 0.5, // Triggers when any part of element is in the zone
					}
				);
				inViewCleanups.push(cleanup);
			} catch (error) {
				console.error("Error setting up inView:", error);
			}
		});

		// Handle resize
		let resizeTimeout;
		window.addEventListener("resize", () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				if (isMobile()) {
					positionMediaForMobile();
				}
			}, 100);
		});

		// Initial setup
		positionMediaForMobile();

		// Cleanup function (call this if you need to destroy the functionality)
		section._cleanup = () => {
			inViewCleanups.forEach((cleanup) => cleanup());
		};
	});
} */

function moveImage() {
	let section = document.querySelectorAll("[data-hoverList]");
	if (section.length === 0) return;

	const isMobile = () => window.innerWidth < 768;

	section.forEach((section) => {
		let listRows = section.querySelectorAll("[data-hoverList-row]"),
			mediaContainer = section.querySelector("[data-hover-media]"),
			media = mediaContainer.querySelectorAll("[data-counter]"),
			offset = mediaContainer.getBoundingClientRect().height / 2,
			targetX = 0,
			targetY = 0,
			currentX = 0,
			currentY = 0,
			rafActive = false;
		const ease = 0.2;
		let mouseMoveThrottle = false;
		let scrollRAF = null;
		let currentActiveRow = null;

		// Mobile: Position media container centrally
		function positionMediaForMobile() {
			if (isMobile()) {
				mediaContainer.style.transform = `translate(${0}px, ${0}px)`;
			}
		}

		// Desktop: Mouse tracking
		section.addEventListener("mousemove", (e) => {
			if (isMobile()) return;

			const rect = section.getBoundingClientRect();

			if (mouseMoveThrottle) return;
			mouseMoveThrottle = true;
			setTimeout(() => (mouseMoveThrottle = false), 1);

			targetX = e.clientX - rect.left;
			targetY = e.clientY - rect.top - offset;
			if (!rafActive) {
				rafActive = true;
				requestAnimationFrame(moveMediaContainer);
			}
		});

		function moveMediaContainer() {
			if (mediaContainer.length === 0) return;

			currentX += (targetX - currentX) * ease;
			currentY += (targetY - currentY) * ease;

			mediaContainer.style.transform = `translate(${currentX}px, ${currentY}px)`;
			if (
				Math.abs(targetX - currentX) > 0.1 ||
				Math.abs(targetY - currentY) > 0.1
			) {
				requestAnimationFrame(moveMediaContainer);
			} else {
				rafActive = false;
			}
		}

		// Mobile: Check which row is at center of viewport
		function checkCenterLine() {
			if (!isMobile()) return;

			const viewportCenter = window.innerHeight - window.innerHeight / 4;
			let closestRow = null;
			let closestDistance = Infinity;

			listRows.forEach((row) => {
				const rect = row.getBoundingClientRect();
				const rowCenter = rect.top + rect.height / 2;
				const distance = Math.abs(viewportCenter - rowCenter);

				// Check if row intersects with center line (within row height)
				if (distance < rect.height / 2) {
					if (distance < closestDistance) {
						closestDistance = distance;
						closestRow = row;
					}
				}
			});

			// Update active row
			if (closestRow !== currentActiveRow) {
				// Hide previous
				if (currentActiveRow) {
					currentActiveRow.classList.remove("!opacity-100");
					const prevLink = currentActiveRow.querySelector("a");
					if (prevLink) {
						prevLink.classList.remove("underline");
					}
				}

				currentActiveRow = closestRow;

				if (currentActiveRow) {
					// Show new
					currentActiveRow.classList.add("!opacity-100");
					const currentLink = currentActiveRow.querySelector("a");
					if (currentLink) {
						currentLink.classList.add("underline");
					}
					const counter =
						currentActiveRow.getAttribute("data-counter");
					showMediaForCounter(counter);
				} else {
					// No row at center, hide all
					hideAllMedia();
				}
			}
		}

		function showMediaForCounter(counter) {
			mediaContainer.classList.remove("opacity-0");
			media.forEach((element) => {
				if (element.getAttribute("data-counter") === counter) {
					element.classList.remove("opacity-0");
				} else {
					element.classList.add("opacity-0");
				}
			});
		}

		function hideAllMedia() {
			mediaContainer.classList.add("opacity-0");
		}

		// Mobile: Scroll listener with RAF
		function onScroll() {
			if (!isMobile()) return;

			if (scrollRAF) return;
			scrollRAF = requestAnimationFrame(() => {
				checkCenterLine();
				scrollRAF = null;
			});
		}

		// Desktop: Row interactions
		listRows?.forEach((row) => {
			const counter = row.getAttribute("data-counter");

			const showMedia = () => {
				showMediaForCounter(counter);
			};

			const hideMedia = () => {
				hideAllMedia();
			};

			// Desktop: Mouse events
			row.addEventListener("mouseenter", () => {
				if (!isMobile()) showMedia();
			});
			row.addEventListener("mouseleave", () => {
				if (!isMobile()) hideMedia();
			});

			// Keyboard
			row.addEventListener("focusin", showMedia);
			row.addEventListener("focusout", hideMedia);

			row.addEventListener("focus", () => {
				if (isMobile()) return;

				const rect = row.getBoundingClientRect();
				const sectionRect = section.getBoundingClientRect();

				const newX = rect.left - sectionRect.left + rect.width + 16;
				const newY =
					rect.top - sectionRect.top + rect.height / 2 - offset;

				targetX = newX;
				targetY = newY;

				if (!rafActive) {
					rafActive = true;
					requestAnimationFrame(moveMediaContainer);
				}
			});
		});

		// Mobile: Setup scroll listener
		if (isMobile()) {
			window.addEventListener("scroll", onScroll, {passive: true});
			// Initial check
			checkCenterLine();
		}

		// Handle resize
		let resizeTimeout;
		window.addEventListener("resize", () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				const wasMobile = currentActiveRow !== null;

				if (isMobile()) {
					positionMediaForMobile();
					if (!wasMobile) {
						window.addEventListener("scroll", onScroll, {
							passive: true,
						});
					}
					checkCenterLine();
				} else {
					if (wasMobile) {
						window.removeEventListener("scroll", onScroll);
						currentActiveRow = null;
						hideAllMedia();
					}
				}
			}, 100);
		});

		// Initial setup
		positionMediaForMobile();

		// Cleanup function
		section._cleanup = () => {
			window.removeEventListener("scroll", onScroll);
			if (scrollRAF) {
				cancelAnimationFrame(scrollRAF);
			}
		};
	});
}

function setupScrollAnimations() {
	function handleFadeElements() {
		const fadeElements = document.querySelectorAll(
			'[data-animation="fade"]'
		);
		if (fadeElements.length === 0) return;

		fadeElements.forEach((element) => {
			element.style.opacity = "0";
			inView(
				element,
				() => {
					animate(
						element,
						{opacity: [0, 1]},
						{
							duration: 1,
							ease: easeOutExpo,
						}
					);
				},
				{margin: "-10%"}
			);
		});
	}

	function handleBlobElements() {
		const textBlobs = document.querySelectorAll('[data-animation="blob"]');
		if (textBlobs.length === 0) return;

		textBlobs.forEach((element) => {
			inView(element, () => {
				animate(
					element,
					{scale: [0, 1]},
					{
						duration: 0.6,
						ease: easeOutExpo,
					}
				);
			});
		});
	}

	function handleFooterLogo() {
		const footerLogo = document.querySelector("footer");
		const svgParts = footerLogo.querySelectorAll(".svgmilk");

		inView(
			footerLogo,
			() => {
				const footerAnimation = animate(
					svgParts,
					{y: [400, 0], opacity: [0, 1]},
					{
						duration: 1,
						ease: easeOutExpo2,
						delay: stagger(0.1, {startDelay: 0.4}),
					}
				);

				return (leaveInfo) => footerAnimation.stop();
			},
			{margin: "-5%"}
		);
	}

	function handleBottomsUpAnimation() {
		const element = document.querySelectorAll(
			'[data-animation="bottomsup"]'
		);

		if (element.length === 0) return;

		element.forEach((element) => {
			element.style.transformOrigin = "bottom center";
			element.style.display = "inline-block";
		});

		element.forEach((element) => {
			inView(
				element,
				() => {
					animate(
						element,
						{scaleY: [1, -1], y: [0, "-50%"]},
						{
							duration: 1,
							ease: easeOutExpo,
						}
					);
				},
				{margin: "0px 0px -50% 0px"}
			);
		});
	}

	handleFadeElements();
	handleBlobElements();
	handleFooterLogo();
	handleBottomsUpAnimation();
}

function cowGoesMoo() {
	const dot = document.getElementById("milk-i-dot");

	const sounds = [
		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cow-mooing-type-01.mp3",
		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cow-mooing-type-02.mp3",
		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cow-mooing-type-03.mp3",
		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cow-mooing-type-04.mp3",

		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cow-mooing-type-01.mp3",
		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cow-mooing-type-02.mp3",
		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cow-mooing-type-03.mp3",
		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cow-mooing-type-04.mp3",
		"/_private/wp-content/themes/fresh_milk-v1.0.1/assets/sound/cat-mooing-type-01.mp3",
	];

	function getRandomSound() {
		const index = Math.floor(Math.random() * sounds.length);
		return window.location.origin + sounds[index];
	}

	dot.addEventListener("click", () => {
		playSound(getRandomSound(), {
			pitch: 1,
			speed: 1,
			lowpassFrequency: null,
			highpassFrequency: null,
		});
	});
}

function playSound(
	soundFilePath,
	options = {
		pitch: 1,
		speed: 1,
		lowpassFrequency: null,
		highpassFrequency: null,
	}
) {
	const audioContext = new (window.AudioContext ||
		window.webkitAudioContext)();
	const audioElement = new Audio(soundFilePath);
	const source = audioContext.createMediaElementSource(audioElement);

	if (audioElement == null || undefined) {
		console.warn("soundfile-not-found");
		return;
	}

	// Apply pitch/speed (playbackRate)
	audioElement.playbackRate = options.speed * options.pitch;

	// Apply filters
	let lastNode = source;

	if (options.lowpassFrequency !== null) {
		const lowpassFilter = audioContext.createBiquadFilter();
		lowpassFilter.type = "lowpass";
		lowpassFilter.frequency.setValueAtTime(
			mergedOptions.lowpassFrequency,
			audioContext.currentTime
		);
		lastNode.connect(lowpassFilter);
		lastNode = lowpassFilter;
	}

	if (options.highpassFrequency !== null) {
		const highpassFilter = audioContext.createBiquadFilter();
		highpassFilter.type = "highpass";
		highpassFilter.frequency.setValueAtTime(
			mergedOptions.highpassFrequency,
			audioContext.currentTime
		);
		lastNode.connect(highpassFilter);
		lastNode = highpassFilter;
	}

	// Connect the final node to the destination (speakers)
	lastNode.connect(audioContext.destination);

	// Play the audio
	audioElement.play();

	// Return the audio element, context, and source for potential later manipulation
	return {
		audioElement,
		audioContext,
		source,
	};
}

function newsletterModal(YTimeIn, FadeTimeIn, YTimeOut, FadeTimeOut) {
	const toggles = document.querySelectorAll('a[href="#nyhetsbrev"]');
	const closeModalButton = document.querySelector("#closeNewsletterModal");
	const modal = document.getElementById("newsletterModal");
	const backdrop = document.getElementById("newsletterModalBackdrop"); // Get the backdrop element

	let lastFocusedElement = null; // Variable to store the element that opened the modal

	// Function to open the modal
	function openModal() {
		if (!modal) return; // Exit if modal element is not found

		// Store the element that currently has focus (the button that was clicked)
		lastFocusedElement = document.activeElement;

		modal.style.display = "block"; // Show the modal
		modal.setAttribute("aria-hidden", "false");
		modal.setAttribute("tabindex", "-1"); // Make modal focusable for initial focus

		animate(
			modal,
			{y: ["100%", 0]},
			{
				duration: YTimeIn,
				ease: easeOutExpo2,
			}
		);

		// Show the backdrop
		if (backdrop) {
			backdrop.style.display = "block";
			backdrop.setAttribute("aria-hidden", "false"); // Set to false when visible, or keep true if purely visual
			animate(
				backdrop,
				{opacity: [0, 1]},
				{
					duration: FadeTimeIn,
					ease: easeOutExpo2,
				}
			);
		}

		// Focus the first interactive element inside the modal, typically the close button
		const firstFocusableElement = modal.querySelector(
			".modal-close-button, input, button, a[href]"
		);
		if (firstFocusableElement) {
			firstFocusableElement.focus();
		} else {
			modal.focus(); // Fallback: focus the modal container itself
		}

		// Optional: Hide main page content from screen readers if not relying solely on aria-modal="true"
		// const mainContent = document.querySelector('main');
		// if (mainContent) {
		//     mainContent.setAttribute('aria-hidden', 'true');
		// }
	}

	// Function to close the modal
	function closeModal() {
		if (!modal) return; // Exit if modal element is not found

		// 1. Return focus to the element that opened the modal BEFORE hiding it
		if (lastFocusedElement) {
			lastFocusedElement.focus();
			lastFocusedElement = null; // Clear the stored element
		} else {
			// Fallback: If no previous focus, send focus to body (less ideal but prevents focus loss)
			document.body.focus();
		}

		// 2. Hide the modal

		animate(
			modal,
			{y: [0, "100%"]},
			{
				duration: YTimeOut,
				ease: easeOutExpo2,
			}
		).then(() => {
			modal.style.display = "none";
			modal.setAttribute("aria-hidden", "true"); // Update aria-hidden after hiding
		});

		// 3. Hide the backdrop
		if (backdrop) {
			animate(
				backdrop,
				{opacity: [1, 0]},
				{
					duration: FadeTimeOut,
					ease: easeOutExpo2,
				}
			).then(() => {
				backdrop.style.display = "none";
				backdrop.setAttribute("aria-hidden", "true");
			});
		}

		// Optional: Unhide main page content from screen readers
		// const mainContent = document.querySelector('main');
		// if (mainContent) {
		//     mainContent.setAttribute('aria-hidden', 'false');
		// }
	}

	// Event listener for all buttons that open the modal
	toggles.forEach((button) => {
		button.addEventListener("click", function (event) {
			event.preventDefault(); // Stop default anchor behavior
			openModal();
		});
	});

	// Event listener for the close button inside the modal
	if (!closeModalButton) {
		return;
	} else {
		closeModalButton.addEventListener("click", closeModal);
	}

	// Event listener for clicking outside the modal (on the backdrop)
	// IMPORTANT: Ensure your backdrop covers the whole screen and is behind the modal.
	// If you click on the modal content itself, this should NOT trigger a close.
	if (backdrop) {
		// Only add listener if backdrop exists
		backdrop.addEventListener("click", function (event) {
			// Check if the click was directly on the backdrop itself, not on the modal content
			if (event.target === backdrop) {
				closeModal();
			}
		});
	}

	// Event listener for Escape key press
	document.addEventListener("keydown", function (event) {
		// Check if the modal is currently open and the escape key was pressed
		if (event.key === "Escape" && modal.style.display === "block") {
			event.preventDefault(); // Prevent default browser behavior (like scrolling)
			closeModal();
		}
	});
}

function analyticsInit(fadeInMs) {
	const consentKey = "cookieConsent";
	const acceptTTL = 60 * 60 * 24 * 180; // 180 days
	const declineTTL = 60 * 60 * 24 * 30; // 30 days
	let lastFocusedElement = null;
	let open = false;

	// Check existing consent
	const consent = getItemWithExpiry(consentKey)?.value;

	if (consent === "accepted") {
		loadGA();
	} else if (!consent) {
		showBanner();
	} else {
		console.warn("Cookie read failed");
	}

	function showBanner() {
		lastFocusedElement = document.activeElement;
		open = !open;

		const banner = document.createElement("div");
		banner.innerHTML = `
        <div class="fixed right-0 bottom-8 z-40 max-w-full text-text p-6 rounded-sm bg-yellow text-black" data-cursor-invert style="transform: translateY(0%);">
            <p>Milk comes with cookies. Read more about our <a href="<?= site_url();?>">Privacy Policy</a>.</p>
            <div class="flex gap-4">
                <button id="cookie-accept" class="underline hover:no-underline">That's cool</button>
                <button id="cookie-decline" class="underline hover:no-underline">No thanks</button>
            </div>
        </div>`;

		document.body.prepend(banner);

		const acceptButton = document.getElementById("cookie-accept");
		const declineButton = document.getElementById("cookie-decline");

		acceptButton.onclick = function () {
			setItemWithExpiry(consentKey, "accepted", acceptTTL);
			loadGA();

			animate(
				banner.querySelector(".fixed"),
				{y: [0, "125%"]},
				{
					duration: fadeInMs / 1000, // convert to seconds
					ease: easeOutExpo2,
				}
			);

			setTimeout(() => {
				banner.remove();
				open = !open;
			}, fadeInMs);
		};

		declineButton.onclick = function () {
			setItemWithExpiry(consentKey, "declined", declineTTL);

			animate(
				banner.querySelector(".fixed"),
				{y: [0, "125%"]},
				{
					duration: fadeInMs / 1000, // convert to seconds
					ease: easeOutExpo2,
				}
			);

			setTimeout(() => {
				banner.remove();
				open = !open;
			}, fadeInMs);
		};
	}

	function loadGA() {
		if (window.gtag) {
			console.log("GA already initialized");
			return;
		} // prevent double init

		window.dataLayer = window.dataLayer || [];
		function gtag() {
			dataLayer.push(arguments);
		}
		window.gtag = gtag;

		gtag("js", new Date());
		gtag("config", "G-QLCNM0GEZ8", {
			anonymize_ip: true,
		});

		const script = document.createElement("script");
		script.async = true;
		script.src = "https://www.googletagmanager.com/gtag/js?id=G-QLCNM0GEZ8";
		document.head.appendChild(script);
	}

	const toggleButton = document.querySelectorAll("button[data-cookie]");
	toggleButton.forEach((button) => {
		button.addEventListener("click", () => {
			if (open === false) {
				lastFocusedElement?.focus();
				showBanner();
			}
		});
	});
}

function milkShake() {
	// Early exit if DeviceMotionEvent not supported (desktop)
	if (
		typeof window.DeviceMotionEvent === "undefined" ||
		window.innerWidth > 768
	) {
		console.log("DeviceMotionEvent not supported - skipping milkshake");
		return;
	}

	function startMotion() {
		console.log("milkshake motion started");
		window.addEventListener("devicemotion", (event) => {
			const acc = event.accelerationIncludingGravity;
			if (!acc) return;

			const x = acc.x || 0;
			const y = acc.y || 0;
			const z = acc.z || 0;
			const total = Math.abs(x) + Math.abs(y) + Math.abs(z);

			// Log motion values to console
			console.log(
				`Motion - x: ${x.toFixed(2)}, y: ${y.toFixed(2)}, z: ${z.toFixed(2)}, total: ${total.toFixed(2)}`
			);

			if (total > 30) {
				alert("Milkshake!!");
			}
		});
	}

	// iOS 13+ with permission API
	if (typeof DeviceMotionEvent.requestPermission === "function") {
		// Check current permission state
		navigator.permissions
			.query({name: "accelerometer"})
			.then((result) => {
				console.log("permission state:", result.state);

				if (result.state === "granted") {
					// Already granted, start immediately
					startMotion();
				} else {
					// Need to request permission, create overlay
					createOverlayForPermission();
				}
			})
			.catch(() => {
				// Fallback: if query fails, just create overlay
				console.log("permission query failed, creating overlay");
				createOverlayForPermission();
			});
	} else {
		// Android / older iOS - no permission needed
		startMotion();
	}

	function createOverlayForPermission() {
		const overlay = document.createElement("div");
		overlay.style.position = "fixed";
		overlay.style.top = "0";
		overlay.style.left = "0";
		overlay.style.width = "100%";
		overlay.style.height = "100%";
		overlay.style.background = "transparent";
		overlay.style.zIndex = "100000";
		overlay.style.cursor = "pointer";
		document.body.appendChild(overlay);

		overlay.addEventListener("click", () => {
			console.log("overlay clicked - requesting motion permission");

			DeviceMotionEvent.requestPermission()
				.then((response) => {
					console.log("permission response:", response);
					if (response === "granted") {
						startMotion();
					} else {
						console.warn("Permission denied");
					}
					overlay.remove();
				})
				.catch((error) => {
					console.error("permission error:", error);
					overlay.remove();
				});
		});
	}
}

function milkShakeDebug() {
	console.log("milkshake debug init");

	// --- Create debug box ---
	const box = document.createElement("div");
	box.id = "motion-debug-box";
	box.style.position = "fixed";
	box.style.bottom = "10px";
	box.style.right = "10px";
	box.style.background = "rgba(0,0,0,0.7)";
	box.style.color = "white";
	box.style.padding = "10px 14px";
	box.style.fontSize = "14px";
	box.style.borderRadius = "8px";
	box.style.zIndex = "99999";
	box.style.fontFamily = "monospace";
	box.style.whiteSpace = "pre";
	box.innerText = "x: -\ny: -\nz: -\n(total: -)";
	document.body.appendChild(box);

	// --- Create invisible overlay ---
	const overlay = document.createElement("div");
	overlay.style.position = "fixed";
	overlay.style.top = "0";
	overlay.style.left = "0";
	overlay.style.width = "100%";
	overlay.style.height = "100%";
	overlay.style.background = "transparent";
	overlay.style.zIndex = "100000";
	overlay.style.cursor = "pointer";
	document.body.appendChild(overlay);

	function updateBox(acc) {
		box.innerText =
			`x: ${acc.x.toFixed(2)}\n` +
			`y: ${acc.y.toFixed(2)}\n` +
			`z: ${acc.z.toFixed(2)}\n` +
			`total: ${(Math.abs(acc.x) + Math.abs(acc.y) + Math.abs(acc.z)).toFixed(2)}`;
	}

	function startMotion() {
		console.log("motion start");
		window.addEventListener("devicemotion", (event) => {
			const acc = event.accelerationIncludingGravity;
			if (!acc) return;

			updateBox(acc);
		});
	}

	overlay.addEventListener("click", () => {
		console.log("overlay clicked");

		if (typeof window.DeviceMotionEvent === "undefined") {
			box.innerText = "DeviceMotionEvent\nnot supported";
			overlay.remove();
			return;
		}

		if (typeof DeviceMotionEvent.requestPermission === "function") {
			DeviceMotionEvent.requestPermission()
				.then((response) => {
					console.log("permission response:", response);
					if (response === "granted") {
						startMotion();
						overlay.remove();
					} else {
						box.innerText = "Permission Denied";
						overlay.remove();
					}
				})
				.catch((error) => {
					console.error("permission error:", error);
					box.innerText = "Error";
					overlay.remove();
				});
		} else {
			startMotion();
			overlay.remove();
		}
	});
}

function HeroMediaRotator() {
	class HeroMediaRotator {
		constructor(container) {
			this.container = container;
			this.mode = container.dataset.heroMode; // 'loop' or 'static'
			this.imageDuration =
				parseInt(container.dataset.imageDuration) || 5000;
			this.totalItems = parseInt(container.dataset.totalItems) || 0;
			this.currentIndex = -1;
			this.items = Array.from(
				container.querySelectorAll(".hero-media-item")
			);
			this.imageTimer = null;
			this.loadedVideos = new Set(); // Track which videos we've loaded

			if (this.totalItems > 0) {
				this.init();
			}
		}

		init() {
			// Disable lazy loading for hero videos by adding a marker class
			this.items.forEach((item) => {
				const video = item.querySelector("video");
				if (video) {
					video.classList.add("hero-video-manual-load");
				}
			});

			// Show random initial item
			this.showRandomItem();

			// Only set up loop mode if enabled and we have multiple items
			if (this.mode === "loop" && this.totalItems > 1) {
				this.setupLoopMode();
			} else if (this.totalItems === 1) {
				// Single item: make videos loop forever
				this.setupSingleItemMode();
			}
		}

		showRandomItem() {
			// Generate random index different from current
			let randomIndex;
			if (this.totalItems === 1) {
				randomIndex = 0;
			} else {
				do {
					randomIndex = Math.floor(Math.random() * this.totalItems);
				} while (randomIndex === this.currentIndex);
			}

			this.showItem(randomIndex);
		}

		showItem(index) {
			// Hide current item
			if (this.currentIndex >= 0) {
				const prevItem = this.items[this.currentIndex];
				prevItem.classList.remove("opacity-100");
				prevItem.classList.add("opacity-0");
				this.pauseCurrentVideo();
			}

			// Clear any existing image timer
			if (this.imageTimer) {
				clearTimeout(this.imageTimer);
				this.imageTimer = null;
			}

			// Show new item
			this.currentIndex = index;
			const currentItem = this.items[this.currentIndex];

			// Load the video if not already loaded
			this.loadVideo(currentItem);

			// Remove opacity-0 and add opacity-100 for Tailwind
			currentItem.classList.remove("opacity-0");
			currentItem.classList.add("opacity-100");

			// Handle video or image
			const video = currentItem.querySelector("video");
			if (video) {
				// Small delay to ensure DOM is ready
				setTimeout(() => this.playCurrentVideo(), 100);
			}

			// Preload next video if in loop mode
			if (this.mode === "loop" && this.totalItems > 1) {
				this.preloadNextVideo();
			}
		}

		loadVideo(item) {
			const video = item.querySelector("video[data-desktop-src]");
			if (!video || this.loadedVideos.has(video)) return;

			// Use your existing responsive video handler if available
			if (typeof window.handleResponsiveVideoSource === "function") {
				window.handleResponsiveVideoSource(video);
			} else {
				// Fallback: simple load
				const desktopSrc = video.dataset.desktopSrc;
				const mobileSrc = video.dataset.mobileSrc;
				const isMobile = window.innerWidth < 768;
				const src = isMobile && mobileSrc ? mobileSrc : desktopSrc;

				const source = video.querySelector("source");
				if (source && src) {
					source.src = src;
					video.load();
				}
			}

			this.loadedVideos.add(video);
		}

		preloadNextVideo() {
			// Get a random next index
			let nextIndex;
			do {
				nextIndex = Math.floor(Math.random() * this.totalItems);
			} while (nextIndex === this.currentIndex);

			const nextItem = this.items[nextIndex];
			const video = nextItem.querySelector("video");

			// Only preload if it's a video
			if (video) {
				this.loadVideo(nextItem);
			}
		}

		setupLoopMode() {
			// Listen for video end events
			this.items.forEach((item, index) => {
				const video = item.querySelector("video");
				if (video) {
					// Remove loop attribute so video ends and triggers our rotation
					video.removeAttribute("loop");
					video.loop = false;

					video.addEventListener("ended", () => {
						if (index === this.currentIndex) {
							this.showNextItem();
						}
					});
				}
			});

			// Set timer for initial item if it's an image
			const currentItem = this.items[this.currentIndex];
			const isImage = !currentItem.querySelector("video");
			if (isImage) {
				this.startImageTimer();
			}
		}

		setupSingleItemMode() {
			// For single items, ensure videos loop and images stay forever
			const item = this.items[0];
			const video = item.querySelector("video");

			if (video) {
				// Ensure video loops forever
				video.setAttribute("loop", "");
				video.loop = true;
			}
			// Images just stay visible forever (no timer needed)
		}

		showNextItem() {
			// Calculate next index (can repeat, just not same as current)
			let nextIndex;
			if (this.totalItems === 1) {
				nextIndex = 0;
			} else {
				do {
					nextIndex = Math.floor(Math.random() * this.totalItems);
				} while (nextIndex === this.currentIndex);
			}

			this.showItem(nextIndex);

			// Start timer if next item is an image
			const nextItem = this.items[nextIndex];
			const isImage = !nextItem.querySelector("video");
			if (isImage) {
				this.startImageTimer();
			}
		}

		startImageTimer() {
			this.imageTimer = setTimeout(() => {
				this.showNextItem();
			}, this.imageDuration);
		}

		playCurrentVideo() {
			const video = this.items[this.currentIndex].querySelector("video");
			if (video) {
				// Reset video to start
				video.currentTime = 0;
				video.play().catch((err) => {
					console.warn("Video autoplay failed:", err);
				});
			}
		}

		pauseCurrentVideo() {
			if (this.currentIndex >= 0) {
				const video =
					this.items[this.currentIndex].querySelector("video");
				if (video) {
					video.pause();
				}
			}
		}

		destroy() {
			if (this.imageTimer) {
				clearTimeout(this.imageTimer);
			}
			this.pauseCurrentVideo();
		}
	}

	// Initialize all hero media containers
	function initHeroMediaRotators() {
		const containers = document.querySelectorAll(".hero-media-container");
		//console.log("Found containers:", containers.length);

		const rotators = [];

		containers.forEach((container) => {
			//console.log("Initializing container:", container);
			rotators.push(new HeroMediaRotator(container));
		});

		// Cleanup on page unload
		window.addEventListener("beforeunload", () => {
			rotators.forEach((rotator) => rotator.destroy());
		});

		return rotators;
	}

	// Try multiple initialization methods to ensure it runs
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initHeroMediaRotators);
	} else {
		// DOM already loaded
		initHeroMediaRotators();
	}
}

function LogoHoverAnimation() {
	const logoElement = document.getElementById("logo-link");
	const player = LogoAnimation.getPlayer();

	let isPlayingIn = false;
	let isPlayingOut = false;
	let hasPlayedIn = false;
	let shouldPlayOutAfterIn = false; // <-- new flag

	logoElement.addEventListener("mouseenter", () => {
		// if we're already in the middle of playing in/out, ignore
		if (isPlayingIn || isPlayingOut) return;

		// cancel any pending out
		shouldPlayOutAfterIn = false;

		isPlayingIn = true;
		LogoAnimation.playIn();

		player.addEventListener(
			"complete",
			function handlePlayInComplete() {
				isPlayingIn = false;
				player.removeEventListener("complete", handlePlayInComplete);
				hasPlayedIn = true;

				// If the user already left during playIn, immediately playOut
				if (shouldPlayOutAfterIn || !logoElement.matches(":hover")) {
					triggerPlayOut();
				}
			},
			{once: true}
		);
	});

	logoElement.addEventListener("mouseleave", () => {
		// If we're still playing in, mark that we should play out after it’s done
		if (isPlayingIn) {
			shouldPlayOutAfterIn = true;
			return;
		}

		// If we're already playing out or haven't played in yet, ignore
		if (isPlayingOut || !hasPlayedIn) return;

		triggerPlayOut();
	});

	function triggerPlayOut() {
		if (isPlayingOut) return; // prevent double fire

		isPlayingOut = true;
		isPlayingIn = false;
		hasPlayedIn = false;
		shouldPlayOutAfterIn = false;

		LogoAnimation.playOut();

		player.addEventListener(
			"complete",
			function handlePlayOutComplete() {
				isPlayingOut = false;
				player.removeEventListener("complete", handlePlayOutComplete);
			},
			{once: true}
		);
	}
}

const heroFadeOutOnScroll = (() => {
	const parentContainer = document.querySelector(".parent-container");
	const fadeContainer = document.querySelector(".fade-container");
	if (!parentContainer || !fadeContainer) return;

	scroll(animate(fadeContainer, {opacity: [0, 1]}), {
		target: parentContainer,
		offset: ["40vh start", "end end"],
	});
})();

const LogoAnimation = (() => {
	let player = null;
	let isLoaded = false;
	let queuedAction = null;

	function init() {
		const el = document.getElementById("lottie-nav");
		if (!el) return;

		const src = el.getAttribute("data-src");

		isLoaded = false;
		queuedAction = null;

		player = new DotLottie({
			canvas: el,
			src,
			autoplay: false,
			loop: false,
			speed: 1.5,
		});

		player.addEventListener("load", onLoad);

		// 🔴 Critical fix for cached assets
		if (player.isLoaded) {
			onLoad();
		}
	}

	function onLoad() {
		if (isLoaded) return;
		isLoaded = true;

		if (queuedAction) {
			const action = queuedAction;
			queuedAction = null;
			action();
		}
	}

	function ensureReady(action) {
		if (!player) {
			queuedAction = action;
			return;
		}

		if (isLoaded || player.isLoaded) {
			isLoaded = true;
			action();
		} else {
			queuedAction = action;
		}
	}

	function playIn() {
		ensureReady(() => {
			player.setSegment(0, 50);
			player.setFrame(0);
			player.play();
		});
	}

	function playOut() {
		ensureReady(() => {
			player.setFrame(50);
			player.setSegment(50, 180);
			player.play();
		});
	}

	function playFull() {
		ensureReady(() => {
			player.setSegment(0, 180);
			player.setFrame(0);
			player.play();
		});
	}

	function playOutIn() {
		playFull();
	}

	function getPlayer() {
		return player;
	}

	return {
		init,
		playIn,
		playOut,
		playFull,
		playOutIn,
		getPlayer, // 👈 restored for hover logic
	};
})();

const loadingAnimation = (() => {
	let player;

	function init() {
		const el = document.getElementById("lottie-nav");
		const src = el.getAttribute("data-src");

		player = new DotLottie({
			canvas: el,
			src: src,
			autoplay: false,
			loop: false,
		});

		player.addEventListener("load", () => {
			//console.log("Lottie loaded, total frames:", player.totalFrames);
		});
	}

	function playFull() {
		if (!player) return;
		player.setSegment(0, 180);
		player.play();
	}

	return {init, playFull};
})();

// Quicklink - Load only in Firefox and Safari
window.addEventListener("load", () => {
	const ua = navigator.userAgent;
	const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
	const isFirefox = /firefox/i.test(ua);

	//console.log(isFirefox + " - " + isSafari);

	if (isSafari || isFirefox) {
		listen({
			limit: 12, // Only prefetch up to 12 links per page
			throttle: 3, // Only allow 3 concurrent prefetch requests
			urls: ["/case", "/om-oss", "/kontakt", "/nyheter"],
			ignores: [
				(uri) => uri.endsWith(".pdf"), // Ignore PDF links
				/^\/wp-admin\//, // Ignore URLs starting with /wp-admin/
			],
		});
	}
});

/* Load functions */
// Initialize immediately if DOM is already loaded, otherwise wait for DOMContentLoaded
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initAllFunctions);
} else {
	// DOM already loaded (cached page or script loaded late)
	initAllFunctions();
}

function initAllFunctions() {
	//toggleReducedMotion(); // Manual toggle of animations
	//toggleDarkmode();
	//addImageSize();
	//setupResizeListener(addImageSize);
	reducedMotion();
	lazyLoadVimeoEmbed();
	HeroMediaRotator();
	lazyLoadVideos();
	analyticsInit(500);
	customCursor();
	filterCase();
	toggleTabsContent(".tab-buttons button", ".tab-content > div");
	cowGoesMoo();
	loadSlides();
	LogoAnimation.init();
	LogoHoverAnimation();
	if (localStorage.getItem("reduced-motion") !== "true") {
		newsletterModal(0.8, 0.6, 0.6, 0.7);
		detailsAccordionAnimation();
		pageFadeTransition(300);
		setupScrollAnimations();
		moveImage();
		toggleOverlays((duration = 0.8));
		//milkShake();
	} else {
		newsletterModal(0, 0, 0, 0);
		pageFadeTransition(800); // Slows down fade effect
		toggleOverlays((duration = 0));
	}
}
